// Library
import chroma from "chroma-js";

// Constants
import { MODES, VALID_MODES } from "Constants/theme";
import { mainThemeColours } from "Constants/ColourConstants";

// Converts a string to a consistent hex colour code
const stringToColour = (string) => {
  let hash = 0;
  let colour = "#";
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += `00${value.toString(16)}`.slice(-2);
  }

  return colour;
};

// Maps a value from one range to another, ensuring the value is within the source range
const mapToRange = (value, sourceMin, sourceMax, targetMin, targetMax) => {
  const boundedValue = Math.min(Math.max(value, sourceMin), sourceMax);
  const normalizedValue = (boundedValue - sourceMin) / (sourceMax - sourceMin);

  return targetMin + normalizedValue * (targetMax - targetMin);
};

// Generates a muted background colour for avatars based on the input string
const getMutedAvatarColour = (string) => {
  const baseColour = stringToColour(string);
  const [light, sat, hue] = chroma(baseColour).lch();

  // Find Colours in range that are more muted
  const adjustedLight = mapToRange(light, 0, 100, 70, 80);
  const adjustedSaturation = mapToRange(sat, 0, 100, 20, 40);

  return chroma
    .lch(adjustedLight, adjustedSaturation, Number.isFinite(hue) ? hue : 0)
    .hex();
};

// Determines whether to use light or dark text based on contrast with the background colour
const getHigherContrastTextColour = (backgroundColour) =>
  chroma.contrast(mainThemeColours.text.light, backgroundColour) >=
  chroma.contrast(mainThemeColours.text.dark, backgroundColour)
    ? mainThemeColours.text.light
    : mainThemeColours.text.dark;

// Generates avatar properties (background colour and initials) based on the input name string
export const stringAvatar = (name) => {
  const parts = name.split(" ").filter(Boolean);
  let initials = "";
  if (parts.length === 0) {
    initials = "?";
  } else if (parts.length === 1) {
    initials = parts[0][0];
  } else {
    initials = `${parts[0][0]}${parts[1][0]}`;
  }
  const backgroundColour = getMutedAvatarColour(name);

  return {
    sx: {
      bgcolor: backgroundColour,
      color: getHigherContrastTextColour(backgroundColour),
    },
    children: initials.toUpperCase(),
  };
};

// Finds the preferred mode (light/dark) based on system settings, with a fallback to light mode
export const getSystemMode = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? MODES.DARK
    : MODES.LIGHT;

// Validates if the provided mode is one of the accepted modes (light, dark, system)
export const isValidMode = (value) => VALID_MODES.has(value);
